import type { Channel } from 'pusher-js'
import Pusher from 'pusher-js'
import api, { axiosInstance } from '@api'
import { useAuthStore } from '@store/auth'
import { storeToRefs } from 'pinia'

Pusher.logToConsole = import.meta.env.VITE_PUSHER_DEBUG === 'true'

let _pusher: Pusher | null = null
let _channel: Channel | null = null
let _channelByUser: Channel | null = null

let _pusherByHash: Pusher | null = null
let _channelByHash: Channel | null = null

let _pusherByOtpHash: Pusher | null = null
let _channelByOtpHash: Channel | null = null

export enum PusherEventType {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

export function usePusherWorkspaceEvent<T>(event: string): Ref<{ type: PusherEventType; data: T } | undefined> {
  const channel = getOrCreateChannel()

  return usePusherEvent(channel, event)
}

export function usePusherByUserEvent<T>(event: string): Ref<{ type: PusherEventType; data: T } | undefined> {
  const channel = getOrCreateChannelByUser()

  return usePusherEvent(channel, event)
}

export function usePusherByHashEvent<T>(auctionHash: string, contactHash: string, event: string): Ref<{ type: PusherEventType; data: T } | undefined> {
  const channel = getOrCreateChannelByHash(auctionHash, contactHash)

  return usePusherEvent(channel, event)
}

function usePusherEvent<T>(channel: Channel | null, event: string): Ref<{ type: PusherEventType; data: T } | undefined> {
  const resultRef = ref<{ type: PusherEventType; data: T }>()

  const callback = (data: { type: PusherEventType; data: T }) => {
    resultRef.value = data
  }

  channel?.bind(event, callback)

  onUnmounted(() => {
    channel?.unbind(event, callback)
  })

  return resultRef
}

export type PusherOnlineStatus = Ref<boolean>

export function usePusherWorkspaceOnlineStatus(): PusherOnlineStatus {
  return usePusherOnlineStatus(getOrCreatePusher())
}

export function usePusherByHashOnlineStatus(): PusherOnlineStatus {
  return usePusherOnlineStatus(getOrCreatePusherByHash())
}

export function usePusherByOtpHashOnlineStatus(): PusherOnlineStatus {
  return usePusherOnlineStatus(getOrCreatePusherByOtpHash())
}

function usePusherOnlineStatus(pusher: Pusher | null): PusherOnlineStatus {
  const isOnline = ref(pusher?.connection.state === 'connected')

  const callback = (state: { previous: string; current: string }) => {
    isOnline.value = state.current === 'connected'
  }

  pusher?.connection.bind('state_change', callback)

  onUnmounted(() => {
    pusher?.connection.unbind('state_change', callback)
  })

  return isOnline
}

export function getActivePusherSocketId(): string | undefined {
  return _pusherByOtpHash?.connection.socket_id
    ?? _pusherByHash?.connection.socket_id
    ?? _pusher?.connection.socket_id
}

export function clearPusher() {
  _channel?.unbind_all()
  _channel?.unsubscribe()
  _channel = null
  _channelByUser?.unbind_all()
  _channelByUser?.unsubscribe()
  _channelByUser = null
  _pusher?.unbind_all()
  _pusher?.disconnect()
  _pusher = null

  _channelByHash?.unbind_all()
  _channelByHash?.unsubscribe()
  _channelByHash = null
  _pusherByHash?.unbind_all()
  _pusherByHash?.disconnect()
  _pusherByHash = null
}

function getOrCreateChannel(): Channel | null {
  if (_channel)
    return _channel

  const { currentWorkspace } = storeToRefs(useAuthStore())
  const workspaceId = currentWorkspace.value?.id
  if (!workspaceId)
    return null

  _channel = getOrCreatePusher()?.subscribe(`private-data-ws-${workspaceId}`) || null

  return _channel
}

function getOrCreateChannelByUser(): Channel | null {
  if (_channelByUser)
    return _channelByUser

  const { currentUser } = storeToRefs(useAuthStore())
  const userId = currentUser.value?.id
  if (!userId)
    return null

  _channelByUser = getOrCreatePusher()?.subscribe(`private-data-user-${userId}`) || null

  return _channelByUser
}

function getOrCreatePusher(): Pusher | null {
  if (_pusher)
    return _pusher

  const pusherKey = import.meta.env.VITE_PUSHER_KEY
  if (!pusherKey)
    return null

  _pusher = new Pusher(pusherKey, {
    cluster: import.meta.env.VITE_PUSHER_CLUSTER,
    channelAuthorization: {
      endpoint: axiosInstance.defaults.baseURL + `/api/v1/push/authorize/`,
      transport: 'ajax',
      customHandler: (params, callback) => {
        api.push.pushAuthorizeCreate(params)
          .then(response => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(null, response.data)
          })
          .catch(() => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(new Error('Authorization failed'), null)
          })
      },
    },
  })

  return _pusher
}

function getOrCreateChannelByHash(auctionHash: string, contactHash: string): Channel | null {
  if (_channelByHash)
    return _channelByHash

  _channelByHash = getOrCreatePusherByHash()?.subscribe(`private-data-hash-${auctionHash}@${contactHash}`) || null

  return _channelByHash
}

function getOrCreatePusherByHash(): Pusher | null {
  if (_pusherByHash)
    return _pusherByHash

  const pusherKey = import.meta.env.VITE_PUSHER_KEY
  if (!pusherKey)
    return null

  _pusherByHash = new Pusher(pusherKey, {
    cluster: import.meta.env.VITE_PUSHER_CLUSTER,
    channelAuthorization: {
      endpoint: axiosInstance.defaults.baseURL + `/api/v1/push/authorize-by-hash/`,
      transport: 'ajax',
      customHandler: (params, callback) => {
        api.push.pushAuthorizeByHashCreate(params, { skipAuthToken: true })
          .then(response => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(null, response.data)
          })
          .catch(() => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(new Error('Authorization failed'), null)
          })
      },
    },
  })

  return _pusherByHash
}

export function usePusherByOtpHashEvent<T>(otpHash: string, event: string): Ref<{ type: PusherEventType; data: T } | undefined> {
  const channel = getOrCreateChannelByOtpHash(otpHash)

  return usePusherEvent(channel, event)
}

function getOrCreateChannelByOtpHash(otpHash: string): Channel | null {
  if (_channelByOtpHash)
    return _channelByOtpHash

  _channelByOtpHash = getOrCreatePusherByOtpHash()?.subscribe(`private-data-otp-hash-${otpHash}`) || null

  return _channelByOtpHash
}

function getOrCreatePusherByOtpHash(): Pusher | null {
  if (_pusherByOtpHash)
    return _pusherByOtpHash

  const pusherKey = import.meta.env.VITE_PUSHER_KEY
  if (!pusherKey)
    return null

  _pusherByOtpHash = new Pusher(pusherKey, {
    cluster: import.meta.env.VITE_PUSHER_CLUSTER,
    channelAuthorization: {
      endpoint: axiosInstance.defaults.baseURL + `/api/v1/push/authorize-by-otp-hash/`,
      transport: 'ajax',
      customHandler: (params, callback) => {
        api.push.pushAuthorizeByOtpHashCreate(params, { skipAuthToken: true })
          .then(response => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(null, response.data)
          })
          .catch(() => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(new Error('Authorization failed'), null)
          })
      },
    },
  })

  return _pusherByOtpHash
}
